import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AuthorProfile {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  job_title: string | null;
  expertise_areas: string[] | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  website_url: string | null;
  credentials: string | null;
  author_slug: string | null;
  is_author: boolean | null;
}

/**
 * Fetch author profile by slug
 */
export const useAuthorProfile = (authorSlug: string) => {
  return useQuery({
    queryKey: ["author-profile", authorSlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("author_slug", authorSlug)
        .eq("is_author", true)
        .maybeSingle();

      if (error) throw error;
      return data as AuthorProfile | null;
    },
    enabled: !!authorSlug,
  });
};

/**
 * Fetch posts by author
 */
export const useAuthorPosts = (authorId: string, status: string = "published") => {
  return useQuery({
    queryKey: ["author-posts", authorId, status],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("author_id", authorId)
        .eq("status", status)
        .order("published_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!authorId,
  });
};

/**
 * Get all authors with published posts
 */
export const useAuthors = () => {
  return useQuery({
    queryKey: ["authors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("is_author", true)
        .order("full_name");

      if (error) throw error;
      return data as AuthorProfile[];
    },
  });
};

/**
 * Update author profile
 */
export const useUpdateAuthorProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<AuthorProfile> & { id: string }) => {
      const { data, error } = await supabase
        .from("profiles")
        .update(updates)
        .eq("id", updates.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["author-profile"] });
      queryClient.invalidateQueries({ queryKey: ["authors"] });
      if (data.author_slug) {
        queryClient.invalidateQueries({ queryKey: ["author-profile", data.author_slug] });
      }
      toast.success("Author profile updated");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update profile: ${error.message}`);
    },
  });
};
